import { useState } from "react";
import { Link, Navigate, useNavigate, useSearchParams } from "react-router";
import { post } from "../api";
import { PublicShell } from "../layout";
import { modules } from "../modules";
import { useOrg } from "../org";
import { useSession, ROLE_LABELS } from "../session";
import { Button, Card, ErrorBox, Input, PageHeader, Select, fieldError } from "../ui";

const KINDS = [
  { value: "particulier", label: "Particulier" },
  { value: "sci", label: "SCI" },
  { value: "indivision", label: "Indivision" },
  { value: "professionnel", label: "Professionnel (agence, gestionnaire)" },
];

export function OrgPickerPage() {
  const { memberships, refresh, user } = useSession();
  const [params] = useSearchParams();
  const nav = useNavigate();
  const [form, setForm] = useState({ name: "", kind: "particulier", siren: "" });
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  const creating = params.get("nouveau") === "1" || memberships.length === 0;
  if (memberships.length === 1 && !creating) return <Navigate to={`/espace/${memberships[0].org_id}`} replace />;
  return (
    <PublicShell>
      {memberships.length ? (
        <>
          <h1>Mes espaces</h1>
          <ul className="list-plain">
            {memberships.map((m) => (
              <li key={m.org_id}>
                <Link to={`/espace/${m.org_id}`} className="org-line">
                  <strong>{m.org_name}</strong>
                  <span className="muted small">{ROLE_LABELS[m.role]}</span>
                </Link>
              </li>
            ))}
          </ul>
        </>
      ) : (
        <>
          <h1>Bienvenue {user?.full_name?.split(" ")[0]}</h1>
          <p className="muted">
            Créez votre espace bailleur pour gérer vos logements. Si vous êtes candidat ou locataire, rendez-vous dans <Link to="/moi">votre espace personnel</Link>.
          </p>
        </>
      )}
      {creating ? (
        <form
          className="stack"
          onSubmit={async (e) => {
            e.preventDefault();
            setBusy(true);
            setError(null);
            try {
              const org = await post<{ id: string }>("/api/v1/orgs", { ...form, siren: form.siren || null });
              await refresh();
              nav(`/espace/${org.id}`);
            } catch (err) {
              setError(err);
            } finally {
              setBusy(false);
            }
          }}
        >
          <h2>Nouvel espace</h2>
          <ErrorBox error={error} />
          <Input label="Nom de l’espace" required placeholder="Ex. Mes locations, SCI des Tilleuls" value={form.name} error={fieldError(error, "name")} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          <Select label="Vous louez en tant que" value={form.kind} options={KINDS} onChange={(e) => setForm({ ...form, kind: e.target.value })} />
          {form.kind !== "particulier" ? <Input label="SIREN (facultatif)" inputMode="numeric" value={form.siren} error={fieldError(error, "siren")} onChange={(e) => setForm({ ...form, siren: e.target.value })} /> : null}
          <Button type="submit" busy={busy}>
            Créer l’espace
          </Button>
        </form>
      ) : (
        <p>
          <Link to="/espaces?nouveau=1">+ Créer un autre espace</Link>
        </p>
      )}
    </PublicShell>
  );
}

export function OrgHomePage() {
  const org = useOrg();
  const widgets = modules.filter((m) => m.Dashboard);
  return (
    <div className="page">
      <PageHeader eyebrow="Tableau de bord" title={org.orgName} intro="Ce qui demande votre attention, du premier dossier au dernier loyer." />
      {widgets.length ? (
        <div className="dashboard-grid">
          {widgets.map((m) => {
            const W = m.Dashboard!;
            return <W key={m.name} orgId={org.orgId} />;
          })}
        </div>
      ) : (
        <Card title="Pour commencer">
          <p>Ajoutez un logement, puis ouvrez une campagne de location pour recevoir des dossiers.</p>
        </Card>
      )}
    </div>
  );
}
